import React, { FC, ReactElement, useRef } from 'react';
import {
  Popover as ChakraPopover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Flex,
  ButtonProps,
  PopoverProps,
  PopoverContentProps,
  useOutsideClick,
  PopoverBodyProps,
  Portal,
  useDisclosure,
  PopoverCloseButtonProps,
} from '@chakra-ui/react';

type ChildProps = {
  onClose?: () => void;
};

type Props = {
  triggerElement: React.ReactNode;
  children: ReactElement<ChildProps>; // Ensure children receive the correct prop
  ctaLabel?: string;
  ctaOnClick?: (args) => void;
  secondaryCtaLabel?: string;
  secondaryCtaProps?: ButtonProps;
  ctaProps?: ButtonProps;
  secondaryCtaOnClick?: (args) => void;
  showArrow?: boolean;
  showCloseButton?: boolean;
  popoverProps?: PopoverProps;
  popoverBodyProps?: PopoverBodyProps;
  popoverContentProps?: PopoverContentProps;
  popoverActions?: any;
  containerRef?: any;
  popoverCloseButtonProps?: PopoverCloseButtonProps;
};

export const Popover: FC<Props> = ({
  triggerElement,
  children,
  ctaLabel,
  ctaOnClick,
  secondaryCtaLabel,
  ctaProps,
  secondaryCtaProps,
  secondaryCtaOnClick,
  showArrow = true,
  showCloseButton = true,
  popoverProps = {},
  popoverContentProps = {},
  popoverBodyProps = {},
  popoverActions = {},
  containerRef = null,
  popoverCloseButtonProps = {},
}) => {
  const defaultDisclosure = useDisclosure();
  const popoverDisclosure =
    Object.keys(popoverActions).length > 0 ? popoverActions : defaultDisclosure;
  const { isOpen, onOpen, onClose } = popoverDisclosure;
  const popoverRef = useRef(null);
  const childWithProps = React.isValidElement(children)
    ? React.cloneElement(children, { onClose })
    : children;

  useOutsideClick({
    ref: popoverRef,
    handler: onClose,
  });

  const renderContent = () => {
    return (
      <PopoverContent ref={popoverRef} {...popoverContentProps}>
        {showArrow && <PopoverArrow />}
        {showCloseButton && <PopoverCloseButton {...popoverCloseButtonProps} />}
        <PopoverBody {...popoverBodyProps}>{childWithProps}</PopoverBody>

        {(ctaLabel || secondaryCtaLabel) && (
          <PopoverFooter>
            <Flex gap="10px" w="100%" justifyContent="end">
              {secondaryCtaLabel && (
                <Button
                  variant="secondary"
                  onClick={(props) => {
                    if (secondaryCtaOnClick) {
                      secondaryCtaOnClick({ popoverDisclosure, ...props });
                    } else onClose();
                  }}
                  {...secondaryCtaProps}
                >
                  {secondaryCtaLabel}
                </Button>
              )}
              {ctaLabel && (
                <Button
                  variant="primary"
                  onClick={(props) =>
                    ctaOnClick({ popoverDisclosure, ...props })
                  }
                  {...ctaProps}
                >
                  {ctaLabel}
                </Button>
              )}
            </Flex>
          </PopoverFooter>
        )}
      </PopoverContent>
    );
  };

  return (
    <ChakraPopover
      initialFocusRef={null}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      autoFocus={false}
      closeOnBlur={true}
      {...popoverProps}
    >
      {/*@ts-ignore */}
      <PopoverTrigger>{triggerElement}</PopoverTrigger>

      {containerRef ? (
        <Portal containerRef={containerRef}> {renderContent()}</Portal>
      ) : (
        renderContent()
      )}
    </ChakraPopover>
  );
};
